/**
 * The Content tab's editors — one per field kind in contentFields.json
 * (text, long, button, image, items). Built from the shared Make changes
 * panel pieces so they look exactly like the transaction mode's panels.
 */

import { createContext, useContext, useEffect, useState } from 'react'
import { ArrowDown, ArrowUp, Trash2 } from 'lucide-react'
import { PanelNote, PanelSection, PanelSelect, PanelText, VIOLET, VIOLET_DARK, VIOLET_LINE, VIOLET_SOFT, VioletButton } from '@twasta/page-runtime/customize'
import type { Block, ButtonSpec } from '@/pages/designers/page/types'
import { blockSpec, fieldBound, getAt, keyPath, newId, setAt, type ContentField } from './pageOps'

export interface PageOption { obj_name: string; title: string }

/** Opens the application's picture library (upload, pick, or make one with
 *  AI) and hands back the chosen picture's address. Provided by the mode. */
export const PicturePickerContext = createContext<((onPick: (url: string) => void) => void) | null>(null)

type Obj = Record<string, unknown>

/** A longer text that saves on blur (the panel's PanelText, multi-line). */
export function PanelLong({ label, value, onCommit, placeholder }: {
  label: string; value: string; onCommit: (v: string) => void; placeholder?: string
}) {
  const [draft, setDraft] = useState(value)
  useEffect(() => setDraft(value), [value])
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
      <textarea value={draft} rows={Math.min(8, Math.max(2, Math.ceil((draft || '').length / 48)))}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => { if (draft !== value) onCommit(draft) }}
        data-tw-field={`site-edit-text:${label}`}
        style={{
          boxSizing: 'border-box', width: '100%', borderRadius: 6, padding: '7px 10px', resize: 'vertical',
          border: '1px solid var(--color-border, #d5dae1)', font: '400 14px/1.45 inherit', fontFamily: 'inherit',
          background: 'var(--color-bg, #fff)', color: 'var(--color-text, #111827)',
        }} />
    </label>
  )
}

function linkValue(spec: ButtonSpec): string {
  const c = spec.on_click
  if (c?.kind === 'navigate' && c.obj_type === 'S') return `page:${c.obj_name}`
  if (c?.kind === 'external_url' || (!c && spec.url)) return 'web'
  if (!c || c.kind === 'none') return ''
  return 'other'
}

function webUrl(spec: ButtonSpec): string {
  const c = spec.on_click
  return c?.kind === 'external_url' ? c.url : spec.url || ''
}

/** A button: its words and where it goes (a page of this site or a web address). */
export function ButtonEditor({ label, spec, pages, onChange }: {
  label: string; spec: ButtonSpec | undefined; pages: PageOption[]; onChange: (next: ButtonSpec | undefined) => void
}) {
  if (!spec) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
        <VioletButton quiet hook="site-edit-add-button"
          onClick={() => onChange({ id: newId('btn'), label: 'Find out more', variant: 'primary' })}>+ Add a button</VioletButton>
      </div>
    )
  }
  const link = linkValue(spec)
  const options = [
    { value: '', label: 'Nothing yet' },
    ...pages.map((p) => ({ value: `page:${p.obj_name}`, label: `The ${p.title} page` })),
    { value: 'web', label: 'A web address…' },
    ...(link === 'other' ? [{ value: 'other', label: 'Something set in the page designer' }] : []),
  ]
  const setLink = (v: string) => {
    if (v === 'other') return
    const next: ButtonSpec = { ...spec }
    delete next.url
    if (v.startsWith('page:')) next.on_click = { kind: 'navigate', obj_type: 'S', obj_name: v.slice(5) }
    else if (v === 'web') next.on_click = { kind: 'external_url', url: webUrl(spec) || 'https://' }
    else next.on_click = { kind: 'none' }
    onChange(next)
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 10, borderRadius: 8, border: `1px solid ${VIOLET_SOFT}`, background: '#faf8ff' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: VIOLET_DARK }}>{label}</span>
        <button type="button" onClick={() => onChange(undefined)} data-tw-action="site-edit-remove-button"
          style={{ border: 0, background: 'transparent', color: '#b42318', cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}>
          Remove button
        </button>
      </div>
      <PanelText label="Words on the button" value={spec.label || ''} onCommit={(v) => onChange({ ...spec, label: v })} />
      <PanelSelect label="It opens" value={link} options={options} onChange={setLink}
        hint={link === 'other' ? 'This button does something set in the page designer.' : undefined} />
      {link === 'web' && (
        <PanelText label="Web address" value={webUrl(spec)} placeholder="https://"
          onCommit={(v) => onChange({ ...spec, url: undefined, on_click: { kind: 'external_url', url: v.trim() } })} />
      )}
    </div>
  )
}

/** A picture: its web address and, when the block has one, its description. */
export function ImageEditor({ label, url, alt, hasAlt, onChange }: {
  label: string; url: string; alt: string; hasAlt: boolean; onChange: (url: string, alt?: string) => void
}) {
  const pickPicture = useContext(PicturePickerContext)
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end' }}>
        <div aria-hidden="true" style={{
          width: 64, height: 48, borderRadius: 6, flexShrink: 0, border: `1px solid ${VIOLET_LINE}`,
          background: url ? `center / cover no-repeat url("${url.replace(/"/g, '%22')}")` : '#f5f3ff',
        }} />
        <div style={{ flex: 1 }}>
          <PanelText label={label} value={url} placeholder="Paste a picture's web address"
            onCommit={(v) => onChange(v.trim(), undefined)} />
        </div>
      </div>
      {pickPicture && (
        <div>
          <VioletButton quiet hook="site-edit-pick-picture" onClick={() => pickPicture((picked) => onChange(picked, undefined))}>
            Upload or choose a picture…
          </VioletButton>
        </div>
      )}
      {hasAlt && (
        <PanelText label="Describe the picture" value={alt} placeholder="For people who cannot see it"
          onCommit={(v) => onChange(url, v)} />
      )}
    </div>
  )
}

/** Set one field of an object (a dotted key reaches inside it), dropping the
 *  keys it `clears` and writing the ones it `sets`. */
export function setField(obj: Obj, field: ContentField, value: unknown): Obj {
  const path = keyPath(field.key)
  let next = setAt(obj, path, value)
  const parent = path.slice(0, -1)
  for (const k of field.clears || []) next = setAt(next, [...parent, ...keyPath(k)], undefined)
  if (value !== undefined && value !== '') {
    for (const [k, v] of Object.entries(field.sets || {})) next = setAt(next, [...parent, ...keyPath(k)], v)
  }
  return next
}

const read = (obj: Obj, key: string): unknown => getAt(obj, keyPath(key))

/** A date and time (an ISO value) as the browser's own picker. */
function PanelDateTime({ label, value, onCommit }: { label: string; value: string; onCommit: (v: string) => void }) {
  // datetime-local wants `YYYY-MM-DDTHH:MM` (no seconds, no zone).
  const local = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value) ? value.slice(0, 16) : ''
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
      <input type="datetime-local" value={local} data-tw-field={`site-edit-text:${label}`}
        onChange={(e) => { if (e.target.value) onCommit(`${e.target.value}:00`) }}
        style={{
          boxSizing: 'border-box', width: '100%', height: 34, borderRadius: 6, padding: '0 10px',
          border: '1px solid var(--color-border, #d5dae1)', fontFamily: 'inherit', fontSize: 14,
          background: 'var(--color-bg, #fff)', color: 'var(--color-text, #111827)',
        }} />
    </label>
  )
}

/** A field the block takes from data: shown, with where it comes from. */
function BoundField({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{label}</span>
      <span data-tw-screen={`site-edit-bound:${label}`} style={{ fontSize: 13, color: 'var(--color-text-secondary, #6b7280)', padding: '6px 10px', borderRadius: 6, background: '#f5f3ff', border: `1px dashed ${VIOLET_LINE}` }}>
        Filled in from your data, so it changes with it. Change the record, or unlink it in the page designer.
      </span>
    </div>
  )
}

/** The editors for a list of fields of one object (a block, or one item).
 *  `bound` is the block a top-level list belongs to: a field it takes from
 *  data is shown, never edited. */
export function FieldList({ fields, obj, pages, onChange, bound }: {
  fields: ContentField[]; obj: Obj; pages: PageOption[]; onChange: (next: Obj) => void; bound?: Obj
}) {
  return (
    <>
      {fields.map((f) => {
        if (bound && fieldBound(bound, f.key)) return <BoundField key={f.key} label={f.label} />
        if (f.kind === 'text') {
          return <PanelText key={f.key} label={f.label} value={String(read(obj, f.key) ?? '')}
            onCommit={(v) => onChange(setField(obj, f, v))} />
        }
        if (f.kind === 'long') {
          return <PanelLong key={f.key} label={f.label} value={String(read(obj, f.key) ?? '')}
            onCommit={(v) => onChange(setField(obj, f, v))} />
        }
        if (f.kind === 'lines') {
          const lines = read(obj, f.key)
          return <PanelLong key={f.key} label={f.label} value={Array.isArray(lines) ? lines.join('\n') : ''}
            onCommit={(v) => onChange(setField(obj, f, v.split('\n').map((x) => x.trim()).filter(Boolean)))} />
        }
        if (f.kind === 'datetime') {
          return <PanelDateTime key={f.key} label={f.label} value={String(read(obj, f.key) ?? '')}
            onCommit={(v) => onChange(setField(obj, f, v))} />
        }
        if (f.kind === 'button') {
          if (f.key === '@self') {
            return <ButtonEditor key={f.key} label={f.label} spec={obj as unknown as ButtonSpec} pages={pages}
              onChange={(b) => onChange((b || obj) as unknown as Obj)} />
          }
          return <ButtonEditor key={f.key} label={f.label} spec={read(obj, f.key) as ButtonSpec | undefined} pages={pages}
            onChange={(b) => onChange(setField(obj, f, b))} />
        }
        if (f.kind === 'image') {
          return <ImageEditor key={f.key} label={f.label} url={String(read(obj, f.key) ?? '')}
            hasAlt={!!f.altKey} alt={f.altKey ? String(read(obj, f.altKey) ?? '') : ''}
            onChange={(url, alt) => {
              let next = setField(obj, f, url)
              if (f.altKey && alt !== undefined) next = setField(next, { ...f, key: f.altKey, clears: [], sets: {} }, alt)
              onChange(next)
            }} />
        }
        if (f.kind === 'items') {
          const items = read(obj, f.key)
          return <ItemsEditor key={f.key} field={f} items={Array.isArray(items) ? (items as Obj[]) : []} pages={pages}
            onChange={(list) => onChange(setAt(obj, keyPath(f.key), list))} />
        }
        return null
      })}
    </>
  )
}

/** One block's fields (the section panel lists several; a block's ⚙ one). */
export function BlockFields({ block, pages, showTitle, onChange }: {
  block: Block; pages: PageOption[]; showTitle: boolean; onChange: (b: Block) => void
}) {
  const spec = blockSpec(block.type)
  if (!spec) return null
  const obj = block as unknown as Obj
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {showTitle && <PanelSection title={spec.label} />}
      <FieldList fields={spec.fields} obj={obj} pages={pages} bound={obj}
        onChange={(next) => onChange(next as unknown as Block)} />
    </div>
  )
}

/** A repeated part (cards, questions, points…): change, reorder, remove, add. */
export function ItemsEditor({ field, items, pages, onChange }: {
  field: ContentField; items: Obj[]; pages: PageOption[]; onChange: (items: Obj[]) => void
}) {
  const [open, setOpen] = useState<string | null>(null)
  const noun = field.noun || 'item'
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir
    if (j < 0 || j >= items.length) return
    const list = [...items]
    const [x] = list.splice(i, 1)
    list.splice(j, 0, x)
    onChange(list)
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 13, fontWeight: 600 }}>{field.label}</span>
      {items.map((it, i) => {
        const id = String(it.id ?? i)
        const title = String((field.titleKey && it[field.titleKey]) || `${noun[0].toUpperCase()}${noun.slice(1)} ${i + 1}`)
        const isOpen = open === id
        return (
          <div key={id} style={{ border: `1px solid ${isOpen ? VIOLET : VIOLET_SOFT}`, borderRadius: 8, background: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 6px 4px 10px' }}>
              <button type="button" onClick={() => setOpen(isOpen ? null : id)} aria-expanded={isOpen}
                data-tw-action={`site-edit-item:${i}`}
                style={{ flex: 1, textAlign: 'left', border: 0, background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, padding: '6px 0', color: 'inherit' }}>
                {title}
              </button>
              <IconBtn label={`Move ${noun} up`} disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp size={14} /></IconBtn>
              <IconBtn label={`Move ${noun} down`} disabled={i === items.length - 1} onClick={() => move(i, 1)}><ArrowDown size={14} /></IconBtn>
              <IconBtn label={`Remove this ${noun}`} danger onClick={() => onChange(items.filter((_, k) => k !== i))}><Trash2 size={14} /></IconBtn>
            </div>
            {isOpen && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '4px 10px 10px' }}>
                <FieldList fields={field.fields || []} obj={it} pages={pages}
                  onChange={(next) => onChange(items.map((x, k) => (k === i ? next : x)))} />
              </div>
            )}
          </div>
        )
      })}
      {items.length === 0 && <PanelNote>No {noun}s yet.</PanelNote>}
      <div>
        <VioletButton quiet hook={`site-edit-add-item:${field.key}`} onClick={() => {
          const made: Obj = { ...(field.make || {}), id: newId(noun.replace(/\W/g, '') || 'item') }
          if (field.key === 'fields' && typeof made.name === 'string') made.name = `${made.name}_${items.length + 1}`
          onChange([...items, made])
          setOpen(String(made.id))
        }}>+ Add a {noun}</VioletButton>
      </div>
    </div>
  )
}

function IconBtn({ label, onClick, disabled, danger, children }: {
  label: string; onClick: () => void; disabled?: boolean; danger?: boolean; children: React.ReactNode
}) {
  return (
    <button type="button" aria-label={label} title={label} onClick={onClick} disabled={disabled}
      style={{
        width: 28, height: 28, borderRadius: 6, border: `1px solid ${danger ? '#fecaca' : '#ddd6fe'}`,
        background: '#fff', color: danger ? '#b42318' : VIOLET, cursor: disabled ? 'default' : 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', opacity: disabled ? 0.4 : 1, padding: 0,
      }}>{children}</button>
  )
}
